import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PickFeedbackBarProps {
  userId?: string;
  searchQuery: string;
  searchType?: 'ai-search' | 'quick-discover';
  mediaType?: string;
  movies: Array<{ rating?: number }>;
}

/**
 * Persists thumbs-up/down feedback per search into the `pick_feedback` table.
 * Used to iterate ranking quality over time.
 */
export const PickFeedbackBar = ({
  userId,
  searchQuery,
  searchType,
  mediaType,
  movies,
}: PickFeedbackBarProps) => {
  const { toast } = useToast();
  const [given, setGiven] = useState<boolean | null>(null);

  // Reset when the underlying query changes
  useEffect(() => {
    setGiven(null);
  }, [searchQuery, searchType, mediaType]);

  const submit = async (helpful: boolean) => {
    if (given !== null) return;
    setGiven(helpful);
    toast({
      title: helpful ? "Thanks — noted!" : "We'll do better next time",
      description: helpful
        ? "Your feedback helps us fine-tune tonight's lineups."
        : "We'll use this to sharpen future picks.",
    });
    if (!userId) return;
    try {
      const avg = movies.length > 0
        ? Number((movies.reduce((a, m) => a + (m.rating || 0), 0) / movies.length).toFixed(1))
        : null;
      await (supabase.from as any)('pick_feedback').insert({
        user_id: userId,
        search_query: (searchQuery || '').slice(0, 500) || null,
        search_type: searchType || null,
        helpful,
        result_count: movies.length,
        avg_rating: avg,
        media_type: mediaType || null,
      });
    } catch {
      // best-effort — never block UI
    }
  };

  if (!userId) return null;

  return (
    <div className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3 py-1.5 text-xs">
      <span className="font-semibold text-foreground/80">
        {given === null
          ? "Was tonight's lineup good?"
          : given
            ? "Glad you enjoyed it!"
            : "Thanks — we'll do better."}
      </span>
      <Button
        variant="ghost"
        size="sm"
        disabled={given !== null}
        className={`h-7 px-2 hover:text-marquee-bulb ${given === true ? 'text-marquee-bulb' : ''}`}
        onClick={() => submit(true)}
        aria-label="These picks were great"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={given !== null}
        className={`h-7 px-2 hover:text-destructive ${given === false ? 'text-destructive' : ''}`}
        onClick={() => submit(false)}
        aria-label="These picks missed"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};
